import type {
  CheckRun,
  MutationVariables,
  RerunCheckRunModalProps,
  RerunCheckRunModalState
} from '../types'

import { useMutation, useQuery } from '@tanstack/react-query'
import { Checkbox, Col, Input, Modal, notification, Row, Spin, Typography } from 'antd'
import { isAxiosError } from 'axios'
import { Fragment, useEffect, useState } from 'react'

import $api from '../../../../API'
import { rerunActionKeyMapper } from '../constants'
import { isRerunResponse, uniquePredicate } from '../functions'
import { initialRerunCheckRunModalState } from '../types'

export function RerunCheckRunModal({
  checkRunIds: ids,
  onSuccess,
  onError,
  onCancel,
  ...props
}: RerunCheckRunModalProps): JSX.Element {
  const [state, setState] = useState<RerunCheckRunModalState>(initialRerunCheckRunModalState)

  useEffect(() => {
    setState(initialRerunCheckRunModalState)
  }, [ids])

  const query = useQuery({
    queryKey: ['check-runs', ids],

    async queryFn({ queryKey, signal }) {
      const [, ids] = queryKey

      type CheckRunDetails = Pick<
        CheckRun,
        'id' | 'check_register_reports' | 'payment_files' | 'lcr_file' | 'scheduled_files'
      >

      try {
        const response = await $api.post<CheckRunDetails[]>(
          '/check-runs/multiple',
          {
            ids
          },
          {
            signal
          }
        )

        return response.data
      } catch {
        throw new Error('Something went wrong')
      }
    },

    enabled: Boolean(ids)
  })

  const mutation = useMutation(async (payload: MutationVariables) => {
    try {
      const response = await $api.post<{
        scheduled?: boolean
        actions?: Array<{
          key: string
          success: boolean
          reason?: string
        }>
      }>('/check-runs/rerun', payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.status === 204) {
        return
      }
      return response.data
    } catch (e) {
      if (isAxiosError(e)) {
        const data = e.response?.data
        const err = new Error(data?.message ?? 'Failed to rebuild check run') as Error & {
          description?: string
        }
        err.description = data?.description
        throw err
      }
    }
  })

  const uniqueStatusIds =
    query.data?.[0]?.payment_files
      ?.map(file => file.status.id)
      .sort()
      .filter(uniquePredicate) ?? []
  const allPaymentFilesHaveSameStatus = uniqueStatusIds.length === 1
  const statusId = uniqueStatusIds[0]

  const reasonRequired = typeof statusId === 'string' && statusId === '5'
  void allPaymentFilesHaveSameStatus

  return (
    <Modal
      {...props}
      okText={mutation.isLoading ? 'Running...' : 'Rerun'}
      okButtonProps={{
        loading: mutation.isLoading,
        disabled: mutation.isLoading || (reasonRequired && state.reason.trim().length === 0)
      }}
      // width={800}
      onOk={() => {
        if (!Array.isArray(ids)) {
          return
        }

        mutation.mutate(
          {
            ids,
            ...state
          },
          {
            onSuccess(data) {
              if (!data) return

              // Handle new scheduled response (non-blocking rerun)
              if (data.scheduled) {
                notification.info({
                  message: 'Rerun scheduled',
                  description: 'You will be notified when it completes.',
                  duration: 4
                })
                onSuccess?.()
                return
              }

              // Legacy response handling for backwards compatibility
              if (!isRerunResponse(data)) {
                throw new Error('Invalid response')
              }

              const successActions = data.actions.filter(action => action.success)
              const failedActions = data.actions.filter(action => !action.success)

              const notify =
                failedActions.length > 0
                  ? successActions.length > 0
                    ? notification.warning
                    : notification.error
                  : notification.success

              const message =
                failedActions.length > 0
                  ? successActions.length > 0
                    ? 'Rerun partially failed'
                    : 'Rerun failed'
                  : 'Rerun successful'

              notify({
                message,
                description: (
                  <div className='grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs'>
                    {data.actions.map(action => (
                      <Fragment key={action.key}>
                        <span className='text-sm font-semibold text-left text-gray-500'>
                          {rerunActionKeyMapper[action.key] ?? action.key}
                        </span>

                        <span className='text-sm font-semibold text-left uppercase text-lucent-text'>
                          {action.success ? '✅' : '❌'}
                        </span>

                        {!action.success && (
                          <span className='whitespace-pre-wrap text-sm text-lucent-gray text-left -mt-0.5 col-span-2'>
                            Reason: {action.reason}
                          </span>
                        )}
                      </Fragment>
                    ))}
                  </div>
                ),
                duration: 15000
              })

              onSuccess?.()
            },

            onError(error) {
              const err = error as Error & { description?: string }
              notification.error({
                message: err?.message,
                description: err?.description
              })

              onError?.(err)
            }
          }
        )
      }}
      onCancel={() => {
        onCancel?.()
      }}
    >
      {query.isFetching && (
        <div className='flex justify-center items-center h-32'>
          <Spin />
        </div>
      )}

      {!query.isFetching && (
        <>
          <Typography.Text>Please, choose options to rerun the check run.</Typography.Text>

          <Checkbox.Group
            className='pt-2'
            value={[
              ...(state.targetPaymentFilesStatusId === '4' ? ['reset-payment-files'] : []),
              ...(state.resetTotalsChecked ? ['reset-totals'] : []),
              ...(state.rebuildCheckRegisterReports ? ['rebuild-check-register-reports'] : []),
              ...(state.dropOffToLin ? ['drop-off-to-lin'] : []),
              ...(state.emailNotification ? ['email-notification'] : [])
            ]}
            onChange={values => {
              setState(state => ({
                ...state,
                targetPaymentFilesStatusId: values.includes('reset-payment-files')
                  ? '4'
                  : undefined,
                resetTotalsChecked: values.includes('reset-totals'),
                rebuildCheckRegisterReports: values.includes('rebuild-check-register-reports'),

                dropOffToLin: values.includes('rebuild-check-register-reports')
                  ? values.includes('drop-off-to-lin')
                  : false,

                emailNotification: values.includes('rebuild-check-register-reports')
                  ? values.includes('email-notification')
                  : false
              }))
            }}
          >
            <Row className=''>
              <Col className='flex flex-col gap-1'>
                <Row className=''>
                  <Col className=''>
                    <Checkbox value='reset-payment-files' disabled>
                      Reset payment files to status{' '}
                      <span className='font-mono p-1 px-1.5 -m-1 bg-gray-50 rounded'>4</span>{' '}
                      (waiting LuminX PDF File)
                    </Checkbox>
                  </Col>
                </Row>

                <Row className=''>
                  <Col className=''>
                    <Checkbox value='reset-totals' disabled>
                      Reset check run&apos;s{' '}
                      <span className='font-mono p-1 px-1.5 -m-1 bg-gray-50 rounded'>
                        totals_checked
                      </span>{' '}
                      to <span className='font-mono p-1 px-1.5 -m-1 bg-gray-50 rounded'>false</span>
                    </Checkbox>
                  </Col>
                </Row>

                <Row className=''>
                  <Col className=''>
                    <Checkbox value='rebuild-check-register-reports' disabled>
                      Rebuild check register reports
                    </Checkbox>
                  </Col>
                </Row>

                <Row className=''>
                  <Col className=''>
                    <Checkbox value='drop-off-to-lin' disabled={!state.rebuildCheckRegisterReports}>
                      Drop off to LIN &{' '}
                      <span className='font-mono p-1 px-1.5 -m-1 bg-gray-50 rounded'>plink</span>{' '}
                      command execution
                    </Checkbox>
                  </Col>
                </Row>

                <Row className=''>
                  <Col className=''>
                    <Checkbox
                      value='email-notification'
                      disabled={!state.rebuildCheckRegisterReports}
                    >
                      Email notification of external recipients/clients
                    </Checkbox>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Checkbox.Group>

          <div className='flex flex-col gap-1.5 py-2'>
            <label className='px-3 text-sm font-semibold'>
              Reason
              {reasonRequired && (
                <>
                  {' '}
                  <span className='text-red-500'>*</span>
                </>
              )}
            </label>

            <Input.TextArea
              placeholder={
                reasonRequired
                  ? 'Provide a reason for rerunning the check run'
                  : 'Optionally, provide a reason for rerunning the check run'
              }
              value={state.reason}
              onChange={event => {
                setState({
                  ...state,
                  reason: event.target.value
                })
              }}
              showCount
              required={reasonRequired}
              allowClear={!reasonRequired}
            />
          </div>
        </>
      )}
    </Modal>
  )
}
